import { useCallback, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import { useHoverPopoverContent } from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import { useHoverPopoverSetter } from '@atlassian/jira-hover-popover/src/controllers/hover-popover-state-provider/index.tsx';

const HOVER_DEBOUNCE_DURATION = 100;

export const useEventDelegator = () => {
	const { setPopoverClosed, setPopoverOpen } = useHoverPopoverSetter();
	const { getCellIdByRef } = useHoverPopoverContent();

	const currentHoveredCellFieldIdRef = useRef<string | null>(null);
	const debouncedSetHoverFocusStateRef = useRef<ReturnType<typeof debounce> | null>(null);

	const clearHoverFocusState = useCallback(() => {
		debouncedSetHoverFocusStateRef.current?.cancel();
		setPopoverClosed();
		currentHoveredCellFieldIdRef.current = null;
	}, [setPopoverClosed]);

	const setHoverFocusState = useCallback(
		(fieldId: string, cell: HTMLTableCellElement) => {
			setPopoverOpen({
				fieldId,
				cell,
				clearHoverFocusState,
			});
		},
		[setPopoverOpen, clearHoverFocusState],
	);

	const handleMouseOver = useCallback(
		(event: React.MouseEvent<HTMLTableElement>) => {
			if (event.target instanceof HTMLElement) {
				const target = event.target;
				// Get closest cell that is being hovered over
				const cell = target.closest('td');
				// If no cell is found, clear the hover state and return
				if (!cell || !(cell instanceof HTMLTableCellElement)) {
					clearHoverFocusState();
					return;
				}
				const fieldId = getCellIdByRef(cell);

				// If there is a cell but no field id, clear the hover state and return
				if (!fieldId) {
					clearHoverFocusState();
					return;
				}

				// If the cell is the same as the current hovered cell, return
				if (fieldId !== currentHoveredCellFieldIdRef.current) {
					// If the cell is different from the current hovered cell, clear the previous hover state
					if (currentHoveredCellFieldIdRef.current) {
						clearHoverFocusState();
					}

					// Finally, set the hover state for the new cell
					currentHoveredCellFieldIdRef.current = fieldId;
					debouncedSetHoverFocusStateRef.current = debounce(
						() => setHoverFocusState(fieldId, cell),
						HOVER_DEBOUNCE_DURATION,
					);
					debouncedSetHoverFocusStateRef.current();
				}
			}
		},
		[clearHoverFocusState, getCellIdByRef, setHoverFocusState],
	);

	const handleMouseLeave = useCallback(() => {
		clearHoverFocusState();
		debouncedSetHoverFocusStateRef.current?.cancel();
	}, [clearHoverFocusState]);

	useEffect(() => {
		return () => {
			if (currentHoveredCellFieldIdRef.current) {
				clearHoverFocusState();
			}
			debouncedSetHoverFocusStateRef.current?.cancel();
		};
	}, [clearHoverFocusState]);

	return {
		handleMouseLeave,
		handleMouseOver,
	};
};

// Returns a ref to bind to the table cell.
// It adds the table cell element and its corresponding ARI field ID to the cellRefFieldIdMap.
// lastNode ensures that a cell's ref/ARI field ID is removed from the map when the cell is unmounted.
// This is used by the event listener to look up the corresponding ARI field ID for the cell.
export const useEventDelegatorRef = (fieldId?: string) => {
	const lastNode = useRef<HTMLTableCellElement | null>(null);
	const { setCellIdByRef, deleteCellIdByRef } = useHoverPopoverContent();

	const tableCellRef = useCallback(
		(node: HTMLTableCellElement | null) => {
			if (node && fieldId) {
				setCellIdByRef(node, fieldId);
				lastNode.current = node;
			} else if (lastNode.current) {
				deleteCellIdByRef(lastNode.current);
				lastNode.current = null;
			}
		},
		[fieldId, setCellIdByRef, deleteCellIdByRef],
	);

	return tableCellRef;
};
